import { useMutation } from '@tanstack/react-query'
import { normalizeAccountTimeZone } from '@repo/sdk'
import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react'
import type { ReactNode } from 'react'

import { useAuth } from '@/lib/auth'
import { useTRPC } from '@/lib/trpc'

export function browserTimeZone(): string {
  try {
    return normalizeAccountTimeZone(Intl.DateTimeFormat().resolvedOptions().timeZone)
  }
  catch {
    return 'UTC'
  }
}

/** Resolve the immutable account calendar before any day-sensitive request. */
function useResolvedAccountTimeZone() {
  const { actor, authResolved } = useAuth()
  const trpc = useTRPC()
  const { mutateAsync } = useMutation({
    ...trpc.userRouter.initializeTimeZone.mutationOptions(),
    retry: 2,
  })
  const [initialized, setInitialized] = useState<{ userId: string, timeZone: string } | null>(null)
  const [failedUserId, setFailedUserId] = useState<string | null>(null)
  const pending = useRef<{ userId: string, promise: Promise<string> } | null>(null)
  const userId = actor?.id ?? null
  const isInitialized = !!userId && initialized?.userId === userId
  const timeZone = isInitialized && initialized ? initialized.timeZone : browserTimeZone()

  const initialize = useCallback(async (): Promise<string> => {
    if (!userId)
      throw new Error('An account identity is required before initializing its calendar')
    if (initialized?.userId === userId)
      return initialized.timeZone
    if (pending.current?.userId === userId)
      return pending.current.promise

    setFailedUserId(null)
    const promise = mutateAsync({ timeZone: browserTimeZone() })
      .then((result) => {
        setInitialized({ userId, timeZone: result.timeZone })
        setFailedUserId(null)
        return result.timeZone
      })
      .catch((error: unknown) => {
        setFailedUserId(userId)
        throw error
      })
      .finally(() => {
        if (pending.current?.userId === userId)
          pending.current = null
      })
    pending.current = { userId, promise }
    return promise
  }, [initialized, mutateAsync, userId])

  useEffect(() => {
    if (!authResolved || !userId || isInitialized)
      return
    const resolve = () => { void initialize().catch(() => {}) }
    resolve()
    window.addEventListener('focus', resolve)
    window.addEventListener('online', resolve)
    return () => {
      window.removeEventListener('focus', resolve)
      window.removeEventListener('online', resolve)
    }
  }, [authResolved, initialize, isInitialized, userId])

  return {
    timeZone,
    ready: authResolved && (!userId || isInitialized),
    isError: !!userId && failedUserId === userId,
    initialize,
  }
}

const AccountTimeZoneContext = createContext<ReturnType<typeof useResolvedAccountTimeZone> | null>(null)

export function AccountTimeZoneProvider({ children }: { children: ReactNode }) {
  const calendar = useResolvedAccountTimeZone()
  return <AccountTimeZoneContext.Provider value={calendar}>{children}</AccountTimeZoneContext.Provider>
}

export function useAccountTimeZone() {
  const calendar = useContext(AccountTimeZoneContext)
  if (!calendar)
    throw new Error('AccountTimeZoneProvider is required')
  return calendar
}
